import { useEffect, useState } from "react";
import {
  Alert, AlertIcon, Box, Button, Flex, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Spinner, Tbody, Td, Text, Th, Thead, Tr, useToast,
} from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";
import { ChildrenPreview, createChildren, getChildrenPreview } from "../../../services/fiscalService";
import { crm } from "../../../theme";
import { TableCard, tdProps, thProps } from "./tableStyles";
import { decimalInput, parseDecimal, parseWeight } from "./fiscalLabels";

type Row = { container: string; volumeQuantity: string; volumeKind: string; netWeight: string; grossWeight: string; qty: Record<number, string> };

/** Quantidade digitada (pt-BR); vazio = 0, inválido = NaN. */
const parse = (v: string) => parseDecimal(v) ?? 0;
const fmt = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: 4 });

/**
 * Gerar NFs filhote da remessa (NF mãe) autorizada: uma por contêiner/caminhão, com a quantidade de cada item.
 * Gera os rascunhos de uma vez (tudo ou nada); cada filhote é emitida na tela da nota.
 */
const ChildrenModal = ({ motherId, onClose, onCreated }: { motherId: string; onClose: () => void; onCreated: () => void }) => {
  const toast = useToast();
  const [preview, setPreview] = useState<ChildrenPreview | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const newRow = (p: ChildrenPreview, container = ""): Row =>
    ({ container, volumeQuantity: "", volumeKind: p.volumeKind ?? "", netWeight: "", grossWeight: "", qty: {} });

  useEffect(() => {
    getChildrenPreview(motherId)
      .then((p) => {
        setPreview(p);
        setRows(p.containers.length ? p.containers.map((c) => newRow(p, c)) : [newRow(p)]);
      })
      .catch(() => { setFailed(true); toast({ status: "error", title: "Não foi possível carregar a NF mãe" }); });
  }, [motherId]);

  const patch = (index: number, field: keyof Row, value: any) => setRows((list) => list.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  const patchQty = (index: number, item: number, value: string) =>
    setRows((list) => list.map((r, i) => (i === index ? { ...r, qty: { ...r.qty, [item]: value.replace(/[^\d.,]/g, "") } } : r)));

  /** Divide o saldo de cada item igualmente entre as filhotes (a última fica com o resto). */
  const splitEvenly = () => {
    if (!preview) return;
    setRows((list) => list.map((r, i) => ({
      ...r,
      qty: Object.fromEntries(preview.items.map((it) => {
        const part = Math.floor((it.remaining / list.length) * 10000) / 10000;
        const value = i === list.length - 1 ? Math.round((it.remaining - part * (list.length - 1)) * 10000) / 10000 : part;
        return [it.itemNumber, value ? decimalInput(value) : ""];
      })),
    })));
  };

  const allocated = (itemNumber: number) => rows.reduce((s, r) => s + parse(r.qty[itemNumber] ?? ""), 0);
  const over = preview?.items.some((it) => allocated(it.itemNumber) - it.remaining > 0.00001) ?? false;
  const notAuthorized = !preview?.motherAuthorized;
  const empty = rows.some((r) => !Object.values(r.qty).some((q) => parse(q) > 0));
  const bad = (n: number | null) => n !== null && (Number.isNaN(n) || n < 0);
  const badNumber = rows.some((r) => Object.values(r.qty).some((v) => bad(parseDecimal(v ?? "")))
    || bad(parseWeight(r.netWeight)) || bad(parseWeight(r.grossWeight)));

  const generate = async () => {
    if (!preview) return;
    setSaving(true);
    const num = (v: string) => parseWeight(v);
    const result = await createChildren(motherId, rows.map((r) => ({
      container: r.container || null,
      volumeQuantity: r.volumeQuantity ? Number(r.volumeQuantity) : null,
      volumeKind: r.volumeKind || null,
      netWeight: num(r.netWeight),
      grossWeight: num(r.grossWeight),
      items: preview.items.map((it) => ({ itemNumber: it.itemNumber, quantity: parse(r.qty[it.itemNumber] ?? "") })).filter((x) => x.quantity > 0),
    })));
    setSaving(false);
    if (!result.success) {
      toast({ status: "error", title: "Não foi possível gerar as filhotes", description: result.errors.join("\n"), duration: 10000, isClosable: true });
      return;
    }
    toast({ status: "success", title: `${result.invoiceIds.length} rascunho(s) de filhote gerado(s)`, description: "Emita cada uma na lista de notas do processo." });
    onCreated();
  };

  return (
    <Modal isOpen onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="17px">Gerar NFs filhote{preview?.motherNumber ? ` · NF mãe nº ${preview.motherNumber}` : ""}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {failed ? (
            <Text fontSize="13px" color={crm.textMuted} py="24px" textAlign="center">Não foi possível carregar a NF mãe. Feche e tente de novo.</Text>
          ) : !preview ? (
            <Flex justify="center" py="32px"><Spinner /></Flex>
          ) : (
            <Box>
              {preview.warnings.map((w, i) => (
                <Alert key={i} status="warning" borderRadius="10px" mb="8px" fontSize="13px"><AlertIcon />{w}</Alert>
              ))}
              <Text fontSize="13px" color={crm.textMuted} mb="10px">
                Uma filhote por contêiner/caminhão. Sem ICMS/IPI (destacados na NF mãe); a filhote que fecha o saldo de um item leva o centavo que sobrar.
                {preview.existing.length > 0 && ` Já existem ${preview.existing.length} filhote(s) desta mãe.`}
              </Text>

              <TableCard>
                <Thead>
                  <Tr>
                    <Th {...thProps}>Item</Th>
                    <Th {...thProps} isNumeric>Qtd. na mãe</Th>
                    <Th {...thProps} isNumeric>Saldo</Th>
                    <Th {...thProps} isNumeric>Nas filhotes abaixo</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {preview.items.map((it) => {
                    const a = allocated(it.itemNumber);
                    return (
                      <Tr key={it.itemNumber}>
                        <Td {...tdProps}>{it.itemNumber} · {it.description}</Td>
                        <Td {...tdProps} isNumeric>{fmt(it.quantity)} {it.unit}</Td>
                        <Td {...tdProps} isNumeric>{fmt(it.remaining)}</Td>
                        <Td {...tdProps} isNumeric color={a - it.remaining > 0.00001 ? "red.500" : undefined} fontWeight="600">{fmt(a)}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </TableCard>

              <Flex justify="space-between" align="center" mt="16px" mb="8px">
                <Text fontSize="13px" fontWeight="700">Filhotes ({rows.length})</Text>
                <Flex gap="8px">
                  <Button size="sm" variant="outline" onClick={splitEvenly}>Dividir saldo igualmente</Button>
                  <Button size="sm" variant="outline" onClick={() => setRows((l) => [...l, newRow(preview)])}>Adicionar filhote</Button>
                </Flex>
              </Flex>
              <Box overflowX="auto">
                <TableCard>
                  <Thead>
                    <Tr>
                      <Th {...thProps}>#</Th>
                      <Th {...thProps}>Contêiner</Th>
                      <Th {...thProps}>Volumes</Th>
                      <Th {...thProps}>Espécie</Th>
                      <Th {...thProps}>Peso líq.</Th>
                      <Th {...thProps}>Peso bruto</Th>
                      {preview.items.map((it) => <Th key={it.itemNumber} {...thProps}>Qtd. item {it.itemNumber}</Th>)}
                      <Th {...thProps} />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((r, i) => (
                      <Tr key={i}>
                        <Td {...tdProps}>{i + 1}</Td>
                        <Td {...tdProps} minW="140px"><Input size="xs" maxLength={20} value={r.container} onChange={(e) => patch(i, "container", e.target.value.toUpperCase())} /></Td>
                        <Td {...tdProps} minW="80px"><Input size="xs" value={r.volumeQuantity} onChange={(e) => patch(i, "volumeQuantity", e.target.value.replace(/\D/g, ""))} /></Td>
                        <Td {...tdProps} minW="110px"><Input size="xs" maxLength={60} value={r.volumeKind} onChange={(e) => patch(i, "volumeKind", e.target.value)} /></Td>
                        <Td {...tdProps} minW="100px"><Input size="xs" inputMode="decimal" value={r.netWeight} onChange={(e) => patch(i, "netWeight", e.target.value)} /></Td>
                        <Td {...tdProps} minW="100px"><Input size="xs" inputMode="decimal" value={r.grossWeight} onChange={(e) => patch(i, "grossWeight", e.target.value)} /></Td>
                        {preview.items.map((it) => (
                          <Td key={it.itemNumber} {...tdProps} minW="100px">
                            <Input size="xs" value={r.qty[it.itemNumber] ?? ""} onChange={(e) => patchQty(i, it.itemNumber, e.target.value)} />
                          </Td>
                        ))}
                        <Td {...tdProps}>
                          <IconButton aria-label="Remover filhote" size="xs" variant="ghost" icon={<FaTrash />} isDisabled={rows.length === 1}
                            onClick={() => setRows((l) => l.filter((_, k) => k !== i))} />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </TableCard>
              </Box>
              {over && <Text mt="8px" fontSize="13px" color="red.500">A soma das filhotes passa o saldo da NF mãe.</Text>}
              {badNumber && <Text mt="8px" fontSize="13px" color="red.500">Há quantidade ou peso inválido: use vírgula para decimais (ex.: 1.250,5 ou 1,25).</Text>}
              {empty && <Text mt="8px" fontSize="13px" color={crm.textMuted}>Toda filhote precisa de pelo menos uma quantidade.</Text>}
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isLoading={saving}
            isDisabled={!preview || notAuthorized || over || empty || badNumber} onClick={generate}>
            Gerar {rows.length} rascunho(s)
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChildrenModal;
